import { FC } from 'react';
import { CheckCircle2, FileText, FileUp, Info, PencilLine, Trash2 } from 'lucide-react';
import { Button } from '@new/common/ui/Button/Button';
import { SortableTable, type SortableTableColumn } from '@new/common/ui/Table/SortableTable';
import { TranscriptStatusBadge } from '@new/features/transcripts/shared/components/StatusBadge';
import type { TranscriptBreakdown } from '@new/features/transcripts/shared/types';
import { Tooltip } from '@new/common/ui/Tooltip/Tooltip';

import { OfficialTranscriptCell } from './OfficialTranscriptCell';

interface TranscriptTableProps {
  transcripts: TranscriptBreakdown[];
  canEdit: boolean;
  isMarkingReady?: boolean;
  isUpdatingTranscript?: boolean;
  onUpload: () => void;
  onAddManual: () => void;
  onOpen: (transcript: TranscriptBreakdown) => void;
  onDelete: (transcript: TranscriptBreakdown) => void;
  onMarkReady: (transcript: TranscriptBreakdown) => void;
  onToggleOfficialRequested: (transcript: TranscriptBreakdown, requested: boolean) => void;
}

export const TranscriptTable: FC<TranscriptTableProps> = ({
  transcripts,
  canEdit,
  isMarkingReady = false,
  isUpdatingTranscript = false,
  onUpload,
  onAddManual,
  onOpen,
  onDelete,
  onMarkReady,
  onToggleOfficialRequested,
}) => {
  const columns: SortableTableColumn<TranscriptBreakdown>[] = [
    {
      key: 'institution',
      header: 'Institution',
      sortable: true,
      className: 'whitespace-normal! break-words',
    },
    {
      key: 'courses',
      header: 'Courses',
      className: 'w-20 text-center',
      render: (_v, row) => row.courses.length,
    },
    {
      key: 'unofficialCredits',
      header: 'Unofficial',
      sortable: true,
      className: 'w-24 text-center',
    },
    {
      key: 'officialCredits',
      header: 'Official',
      className: 'w-24 text-center',
      render: (_v, row) => (row.officialCredits != null ? row.officialCredits : '—'),
    },
    {
      key: 'officialTranscriptRequested',
      header: 'Official transcript',
      className: 'w-40 text-center',
      render: (_v, row) => (
        <OfficialTranscriptCell
          requested={row.officialTranscriptRequested}
          editable={canEdit && row.status === 'draft'}
          disabled={isUpdatingTranscript}
          onToggle={(requested) => onToggleOfficialRequested(row, requested)}
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      className: 'w-32',
      render: (_v, row) => <TranscriptStatusBadge status={row.status} />,
    },
  ];

  columns.push({
    key: 'actions',
    header: '',
    className: 'w-44 text-right',
    render: (_v, row) =>
      row.status === 'draft' ? (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={isMarkingReady}
            leftIcon={<CheckCircle2 className="size-4" />}
            onClick={(e) => {
              e.stopPropagation();
              onMarkReady(row);
            }}
            text="Ready"
            className="text-(--primary-O900)! [font-family:var(--font-arvo)]! [font-size:var(--fs-l5)]! font-normal! leading-[125%]!"
          />
          <button
            type="button"
            aria-label="Remove transcript"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row);
            }}
            className="cursor-pointer text-(--neutral-black-NB600) hover:text-(--danger-D700)"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      ) : null,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="m-0! [font-size:var(--fs-h5)]! font-bold text-(--neutral-black-NB1000)!">
          Your transcripts
        </h3>
        {canEdit && transcripts.length > 0 ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              leftIcon={<PencilLine className="size-4" />}
              onClick={onAddManual}
              text="Manual"
              className="text-(--primary-O900)! [font-family:var(--font-arvo)]! [font-size:var(--fs-l4)]! font-normal! leading-[125%]!"
            />
            <Button
              variant="outline"
              size="sm"
              leftIcon={<FileUp className="size-4" />}
              onClick={onUpload}
              text="Upload"
              className="text-(--primary-O900)! [font-family:var(--font-arvo)]! [font-size:var(--fs-l4)]! font-normal! leading-[125%]!"
            />
          </div>
        ) : null}
      </div>

      <div className="h-89 overflow-y-auto overflow-x-hidden [&_.overflow-x-auto]:mx-0! [&_.overflow-x-auto]:overflow-x-hidden! [&_.inline-block]:px-0! [&_th]:first:pl-3! [&_th]:last:pr-3! [&_td]:first:pl-3! [&_td]:last:pr-3!">
        {transcripts.length === 0 && canEdit ? (
          <div className="relative flex h-full flex-col items-center justify-center gap-4 rounded-[9px] border border-dashed border-(--neutral-black-NB300) bg-[#FFFDFA] px-6 text-center">
            <FileText className="size-8 text-(--neutral-black-NB700)" strokeWidth={1.5} />
            <div className="flex max-w-110 flex-col gap-1">
              <p className="m-0! [font-size:var(--fs-p2)]! font-semibold text-(--neutral-black-NB1000)!">
                Add your transcripts to see your unofficial transfer credits
              </p>
              <p className="m-0! [font-size:var(--fs-p3)]! text-(--neutral-black-NB700)!">
                Upload a transcript file or enter your courses manually to see a provisional
                estimate of which credits will transfer. For an official transfer credit evaluation,
                request that your official transcript be sent directly from the issuing institution
                to Newlane. Once received, Newlane's Transfer Credit Evaluation Team will review it
                and apply all eligible transfer credits.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                leftIcon={<PencilLine className="size-4" />}
                onClick={onAddManual}
                text="Manual"
                className="text-(--primary-O900)! [font-family:var(--font-arvo)]! [font-size:var(--fs-l4)]! font-normal! leading-[125%]!"
              />
              <Button
                variant="outline"
                size="sm"
                leftIcon={<FileUp className="size-4" />}
                onClick={onUpload}
                text="Upload"
                className="text-(--primary-O900)! [font-family:var(--font-arvo)]! [font-size:var(--fs-l4)]! font-normal! leading-[125%]!"
              />
            </div>
            <div className="absolute top-3 right-3">
              <Tooltip
                side="left"
                align="start"
                content={
                  <div className="flex flex-col gap-2">
                    <p className="m-0! font-semibold text-(--neutral-black-NB1000)">
                      AI-Assisted Transcript Processing Disclosure
                    </p>
                    <p className="m-0! text-(--neutral-black-NB800)">
                      If you upload a transcript, Newlane will use AI-assisted technology to
                      extract, organize, and evaluate information from your academic record. This
                      may include personal information contained in your transcript. AI-generated
                      results may contain errors, so extracted information should be reviewed for
                      accuracy.
                    </p>
                    <p className="m-0! text-(--neutral-black-NB800)">
                      If you prefer not to have your transcript processed using AI, you may enter
                      your transcript information manually instead.
                    </p>
                  </div>
                }
              >
                <Info className="size-5 cursor-help text-(--neutral-black-NB500)" />
              </Tooltip>
            </div>
          </div>
        ) : (
          <SortableTable<TranscriptBreakdown>
            data={transcripts}
            columns={columns}
            getRowKey={(row) => row.id}
            onRowClick={onOpen}
            emptyMessage="No transcripts yet"
            emptyDescription="No transcripts were added to this evaluation."
          />
        )}
      </div>
    </div>
  );
};
